import { NodeBE, Schema } from "src/api/flowTypes";
import { updateFlowVersion } from "src/api/flowVersionUpdateIndex";
import { FlowVersionUpdate } from "src/clients/flow-api";

/**
 * Applies queued node updates
 */
export const getOptimistacallyUpdatedNodes = (
  versionId: string,
  baseState: NodeBE[],
): NodeBE[] => {
  const nodesMap: Map<string, NodeBE> = new Map();
  baseState.forEach((node) => {
    nodesMap.set(node.id, node);
  });
  const queuedUpdates = updateFlowVersion.queueStoreHook.getState().queue;
  for (let i = 0; i < queuedUpdates.length; i++) {
    if (queuedUpdates[i].args[0] !== versionId) continue;
    const flowVersionUpdate: FlowVersionUpdate | undefined =
      queuedUpdates[i].args[1];
    const currentNodeUpdates = flowVersionUpdate?.graph?.nodes;
    if (!currentNodeUpdates) continue;
    Object.keys(currentNodeUpdates).forEach((nodeId) => {
      if (!nodesMap.has(nodeId) || !currentNodeUpdates[nodeId]) return;
      Object.keys(currentNodeUpdates[nodeId]).forEach(
        // Not sure how this can be typed correctly, but it won't break existing types under the condition
        // that nodesMap and currentNodeUpdates contain correct node types
        (updatedKey) => {
          const node = nodesMap.get(nodeId);
          if (node) {
            (node as Record<string, unknown>)[updatedKey] = (
              currentNodeUpdates[nodeId] as Record<string, unknown>
            )[updatedKey];
          }
        },
      );
    });
  }
  return Array.from(nodesMap.values());
};

/**
 * Applies queued schema updates
 */
export const getOptimisticallyUpdatedSchema = (
  versionId: string,
  baseState: Schema,
  schemaType: "input" | "output",
): Schema => {
  let latestSchema = baseState;

  const queuedUpdates = updateFlowVersion.queueStoreHook.getState().queue;

  for (let i = 0; i < queuedUpdates.length; i++) {
    if (queuedUpdates[i].args[0] !== versionId) continue;

    const flowVersionUpdate: FlowVersionUpdate | undefined =
      queuedUpdates[i].args[1];

    const currentSchemaUpdate =
      schemaType === "input"
        ? (flowVersionUpdate?.input_schema as Schema)
        : (flowVersionUpdate?.output_schema as Schema);

    if (!currentSchemaUpdate) continue;

    latestSchema = currentSchemaUpdate;
  }

  return latestSchema;
};
